import { useEffect, useMemo } from "react";

import { PalAutoComplete, PalButton, PalDialog, PalTextField, PalTypography } from "@palamar/fe-library";
import { withCardon } from "cardon";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { Grid } from "@mui/material";

import { agentOfflineTerminateMode, agentOfflineTerminateModeOptions } from "~constants";
import { getAgentInfo, getOnlineAgents } from "~store/socket/livechat/actions";
import { selectAgentInfo, selectOnlineAgents } from "~store/socket/livechat/selectors";
import { selectCurrentProject } from "~store/user/selectors";

const AgentOfflineModal = withCardon(
  function SessionCloseModalContent({ existingSessionCount, get }) {
    const agentInfo = useSelector(selectAgentInfo);

    const currentProject = useSelector(selectCurrentProject);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const onlineAgents = useSelector(selectOnlineAgents);

    useEffect(() => {
      dispatch(getAgentInfo());
      dispatch(getOnlineAgents(currentProject.id));
    }, [dispatch, currentProject.id]);

    // const isAgentAvailable = agentInfo?.status === "AV";

    const formik = useFormik({
      enableReinitialize: true,
      initialValues: {
        selectedTerminateMode: agentOfflineTerminateMode.terminate_session,
        targetAgent: "",
        note: "",
      },
      validationSchema: Yup.object().shape({
        selectedTerminateMode: Yup.string().required(t("common.required")),
        targetAgent: Yup.number().when("selectedTerminateMode", {
          is: (value) => value === agentOfflineTerminateMode.transfer_agent,
          then: Yup.number().required(t("common.required")),
        }),
      }),

      onSubmit: (values) => {
        get(values)();
      },
    });
    const handleSelectMode = (e, value) => {
      formik.setFieldValue("selectedTerminateMode", value);
    };

    const handleSelectTargetAgent = (e, value) => {
      formik.setFieldValue("targetAgent", value);
    };

    const handleCancel = () => {
      get(false)();
    };

    const onlineAgentsWithoutMe = useMemo(() => {
      if (!Array.isArray(onlineAgents)) return [];

      return onlineAgents.filter((agent) => agent.id !== agentInfo?.id);
    }, [agentInfo?.id, onlineAgents]);

    return (
      <PalDialog fullWidth maxWidth="sm" open={open} scroll="body" onClose={handleCancel}>
        <PalDialog.Title>{t("component.agentOfflineModal.title")}</PalDialog.Title>
        <PalDialog.Content dividers={false}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <PalTypography variant="body2">
                {t("component.agentOfflineModal.description").format(existingSessionCount)}
              </PalTypography>
            </Grid>
            <Grid item xs={12}>
              <PalAutoComplete
                fullWidth
                isPrimitiveValue
                label={t("component.agentOfflineModal.terminateMode")}
                options={agentOfflineTerminateModeOptions}
                size="small"
                value={formik.values.selectedTerminateMode}
                onChange={handleSelectMode}
              />
            </Grid>
            {formik.values.selectedTerminateMode === agentOfflineTerminateMode.transfer_agent && (
              <>
                <Grid item xs={12}>
                  <PalAutoComplete
                    fullWidth
                    isPrimitiveValue
                    invalid={formik.touched.targetAgent && formik.errors.targetAgent}
                    label={t("component.agentOfflineModal.targetAgent")}
                    labelSelector="name"
                    options={onlineAgentsWithoutMe}
                    size="small"
                    value={formik.values.targetAgent}
                    valueSelector="id"
                    onChange={handleSelectTargetAgent}
                  />
                </Grid>
                <Grid item xs={12}>
                  <PalTextField
                    fullWidth
                    multiline
                    invalid={formik.touched.note && formik.errors.note}
                    label={t("common.note")}
                    minRows={2}
                    size="small"
                    value={formik.values.note}
                    onChange={(e) => formik.setFieldValue("note", e.target.value)}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </PalDialog.Content>
        <PalDialog.Actions>
          <PalButton color="error" variant="contained" onClick={formik.handleSubmit}>
            {t("common.submit")}
          </PalButton>
          <PalButton onClick={handleCancel}>{t("common.cancel")}</PalButton>
        </PalDialog.Actions>
      </PalDialog>
    );
  },
  { destroyOnHide: true }
);

export default AgentOfflineModal;
